import type { Event } from '@sse/schema';
import { CheckCircle2, FileText, ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useState, type DragEvent } from 'react';
import { ApiError } from '../lib/api';
import { MAX_UPLOAD_MB, PREVIEWABLE, mimeOf, uploadOriginal } from '../lib/upload';
import { useSession } from '../lib/session';
import { Card, Field, btnPrimary, btnSecondary, inputCls } from './ui';

type Rights = NonNullable<Event['image']>['rights'];

const MAX_MB = MAX_UPLOAD_MB;

const RIGHTS: Array<{ id: Rights; label: string }> = [
  { id: 'organizer_consent', label: 'Fornita o autorizzata dall’organizzatore' },
  { id: 'source_license', label: 'Licenza della fonte (es. Creative Commons)' },
  { id: 'own', label: 'Nostra' },
];

const REASONS: Record<string, string> = {
  too_small: 'L’immagine è troppo piccola: serve almeno 250 px sul lato corto.',
  too_large: 'L’immagine è troppo grande (oltre 180 megapixel o 30 MB).',
  unsupported: 'Formato non riconosciuto o file danneggiato.',
  not_found: 'Il file caricato non si trova: riprova.',
  unavailable:
    'Elaborazione delle immagini non disponibile in questo ambiente (in locale senza AWS): usa il pannello di dev.',
  failed: 'Elaborazione non riuscita: riprova tra poco.',
};

const sizeMb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;

type Upload =
  | { phase: 'uploading'; file: File; local?: string; pct: number }
  | { phase: 'processing'; file: File; local?: string }
  | { phase: 'done'; file: File; local?: string };

/**
 * Immagine dell'evento, spesso una locandina (formati A): caricamento diretto su S3 con URL
 * firmato, poi la Lambda media crea le varianti e l'evento riceve l'immagine. Durante il
 * caricamento si vedono subito il file scelto e l'avanzamento; alla fine l'anteprima vera.
 */
export function ImageCard({ event, onChange }: { event: Event; onChange: (e: Event) => void }) {
  const { api, config } = useSession();
  const img = event.image;
  const media = config.mediaUrl ?? config.siteUrl;
  const [alt, setAlt] = useState(img?.alt ?? '');
  const [credit, setCredit] = useState(img?.credit ?? '');
  const [rights, setRights] = useState<Rights>(img?.rights ?? 'organizer_consent');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const [upload, setUpload] = useState<Upload | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  useEffect(() => {
    setAlt(img?.alt ?? '');
    setCredit(img?.credit ?? '');
    setRights(img?.rights ?? 'organizer_consent');
    setPreviewFailed(false);
  }, [img?.key, img?.alt, img?.credit, img?.rights]);
  // l'anteprima locale (object URL) si libera quando non serve più
  useEffect(() => () => void (upload?.local && URL.revokeObjectURL(upload.local)), [upload?.local]);

  const fail = (e: unknown) => {
    if (e instanceof ApiError) {
      const reason = (e.body as { reason?: string }).reason;
      setError(
        e.body.error === 'version_conflict'
          ? 'L’evento è cambiato nel frattempo: ricarica la pagina.'
          : (REASONS[reason ?? ''] ?? e.message),
      );
    } else setError(e instanceof Error ? e.message : String(e));
  };

  const start = async (file: File) => {
    setError(null);
    const type = mimeOf(file);
    if (!type) return setError('Formati accettati: JPEG, PNG, WebP, HEIC o PDF.');
    if (file.size > MAX_MB * 1024 * 1024) return setError(`Il file supera i ${MAX_MB} MB.`);
    const local = PREVIEWABLE.has(type) ? URL.createObjectURL(file) : undefined;
    setUpload({ phase: 'uploading', file, ...(local ? { local } : {}), pct: 0 });
    try {
      const uploadKey = await uploadOriginal(api, file, type, (pct) =>
        setUpload((u) => (u?.phase === 'uploading' ? { ...u, pct } : u)),
      );
      setUpload((u) =>
        u ? { phase: 'processing', file: u.file, ...(u.local ? { local: u.local } : {}) } : u,
      );
      const { event: next } = await api<{ event: Event }>(`/events/${event.id}/image`, {
        method: 'POST',
        body: {
          version: event.version,
          uploadKey,
          rights,
          ...(alt.trim() ? { alt: alt.trim() } : {}),
          ...(credit.trim() ? { credit: credit.trim() } : {}),
        },
      });
      setUpload((u) => (u ? { ...u, phase: 'done' } : u));
      onChange(next);
    } catch (e) {
      setUpload(null);
      fail(e);
    }
  };

  const saveMeta = async () => {
    setError(null);
    setBusy('Salvataggio…');
    try {
      const { event: next } = await api<{ event: Event }>(`/events/${event.id}/image`, {
        method: 'POST',
        body: {
          version: event.version,
          rights,
          ...(alt.trim() ? { alt: alt.trim() } : {}),
          ...(credit.trim() ? { credit: credit.trim() } : {}),
        },
      });
      onChange(next);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!window.confirm('Togliere l’immagine dall’evento?')) return;
    setBusy('Rimozione…');
    try {
      const { event: next } = await api<{ event: Event }>(
        `/events/${event.id}/image?version=${event.version}`,
        { method: 'DELETE' },
      );
      setUpload(null);
      onChange(next);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files[0];
    if (f) void start(f);
  };
  const working = upload?.phase === 'uploading' || upload?.phase === 'processing';
  const metaDirty =
    img && (alt !== (img.alt ?? '') || credit !== (img.credit ?? '') || rights !== img.rights);
  const serverPreview = img ? `${media}/media/${img.key}-800.webp` : null;
  const poster = img?.width && img.height ? img.height / img.width >= 1.2 : false;

  return (
    <Card title="Immagine o locandina">
      {/* durante il caricamento: il file scelto, subito */}
      {working && (
        <div className="flex flex-col gap-2">
          <div className="relative flex min-h-40 items-center justify-center bg-surface">
            {upload.local ? (
              <img
                src={upload.local}
                alt=""
                className="max-h-80 w-full object-contain opacity-70"
              />
            ) : (
              <div className="flex flex-col items-center gap-1 p-6 text-sm text-muted">
                <FileText className="size-10" aria-hidden />
                {upload.file.name}
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-fixed-black/70 px-3 py-2 text-xs text-fixed-white">
              <div className="flex items-center gap-2">
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {upload.phase === 'uploading'
                  ? `Caricamento ${upload.pct}% · ${sizeMb(upload.file.size)}`
                  : upload.file.type === 'application/pdf' ||
                      upload.file.name.toLowerCase().endsWith('.pdf')
                    ? 'Preparo la locandina dal PDF…'
                    : 'Preparo le versioni per il sito…'}
              </div>
              <div className="mt-1 h-1 bg-fixed-white/30" aria-hidden>
                <div
                  className="h-1 bg-fixed-yellow transition-[width]"
                  style={{ width: upload.phase === 'uploading' ? `${upload.pct}%` : '100%' }}
                />
              </div>
            </div>
          </div>
          {upload.phase === 'processing' && (
            <p className="text-xs text-muted">
              Di solito pochi secondi; il primo caricamento della giornata può richiederne fino a
              15.
            </p>
          )}
        </div>
      )}

      {/* immagine salvata: anteprima vera, dalle varianti create per il sito */}
      {!working && img && serverPreview && (
        <div className="flex flex-col gap-2">
          {upload?.phase === 'done' && (
            <p role="status" className="flex items-center gap-2 bg-ok-soft p-2 text-sm text-fg">
              <CheckCircle2 className="size-4 shrink-0" aria-hidden />
              Immagine caricata e salvata nell’evento.
            </p>
          )}
          <a
            href={`${media}/media/${img.key}-1600.webp`}
            target="_blank"
            rel="noopener"
            className="block bg-surface"
            title="Apri la versione grande"
          >
            {previewFailed ? (
              upload?.local ? (
                <img
                  src={upload.local}
                  alt={img.alt ?? ''}
                  className="max-h-80 w-full object-contain"
                />
              ) : (
                <span className="block p-6 text-center text-sm text-muted">
                  Anteprima non disponibile qui (l’immagine è salvata).
                </span>
              )
            ) : (
              <img
                src={serverPreview}
                alt={img.alt ?? ''}
                onError={() => setPreviewFailed(true)}
                className="max-h-80 w-full object-contain"
              />
            )}
          </a>
          <p className="text-xs text-muted">
            {img.width ? `${img.width}×${img.height} px` : 'Dimensioni non note'}
            {poster ? ' · locandina verticale: sul sito si vede intera' : ''} · clic per la versione
            grande
          </p>
        </div>
      )}

      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`flex cursor-pointer flex-col items-center gap-1 border-2 border-dashed p-4 text-center text-sm ${
          working ? 'pointer-events-none opacity-50' : ''
        } ${drag ? 'border-fg bg-surface' : 'border-fg/25 hover:bg-surface'}`}
      >
        <ImagePlus className="size-6" aria-hidden />
        <span className="font-medium">
          {img ? 'Sostituisci' : 'Carica la locandina o una foto'}
        </span>
        <span className="text-xs text-muted">
          Trascina qui il file o fai clic · JPEG, PNG, WebP, HEIC o PDF (prima pagina) · fino a{' '}
          {MAX_MB} MB
        </span>
        <input
          type="file"
          className="sr-only"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf"
          disabled={working || Boolean(busy)}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void start(f);
          }}
        />
      </label>
      <Field label="Testo alternativo" hint="Cosa mostra l’immagine, per chi non la vede">
        <input
          className={inputCls}
          value={alt}
          maxLength={300}
          onChange={(e) => setAlt(e.target.value)}
        />
      </Field>
      <Field label="Crediti" hint="Es. foto di…, Pro Loco di…">
        <input
          className={inputCls}
          value={credit}
          maxLength={200}
          onChange={(e) => setCredit(e.target.value)}
        />
      </Field>
      <Field label="Diritti d’uso">
        <select
          className={inputCls}
          value={rights}
          onChange={(e) => setRights(e.target.value as Rights)}
        >
          {RIGHTS.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>
      </Field>
      {busy && (
        <p role="status" className="text-sm">
          {busy}
        </p>
      )}
      {error && (
        <p role="alert" className="bg-danger-soft p-2 text-sm">
          {error}
        </p>
      )}
      {img && !working && (
        <div className="flex flex-wrap gap-2">
          {metaDirty && (
            <button
              type="button"
              className={btnPrimary}
              onClick={saveMeta}
              disabled={Boolean(busy)}
            >
              Salva testo e crediti
            </button>
          )}
          <button type="button" className={btnSecondary} onClick={remove} disabled={Boolean(busy)}>
            <Trash2 className="size-4" aria-hidden /> Togli
          </button>
        </div>
      )}
    </Card>
  );
}
